import { useState, useEffect } from 'react';
import { useArtifactStore } from '../hooks/useArtifactStore';
import type { WidgetProps } from './WidgetRegistry';

interface SkillMeta {
  name: string;
  title: string;
  category: string;
  summary: string;
  authorityRequired: number;
  triggers: string[];
}

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

const AUTHORITY_LABELS: Record<number, string> = {
  0: 'Observer', 1: 'Contributor', 2: 'Operator', 3: 'Architect', 4: 'Admin',
};

export function SkillsPanelWidget(_props: WidgetProps) {
  const { activeArtifact } = useArtifactStore();
  const [skills, setSkills] = useState<SkillMeta[]>([]);
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  const [skillBody, setSkillBody] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${BASE_URL}/barnskills`)
      .then(r => r.json())
      .then(data => setSkills(Array.isArray(data) ? data : []))
      .catch(() => setSkills([]));
  }, []);

  const plane = activeArtifact?.content?.type;
  const relevantSkills = skills.filter(s => {
    if (plane === 'sql') return s.name.startsWith('studio-sql') || s.name === 'studio-verify-tool' || s.name === 'security-review';
    if (plane === 'prompt') return s.name.startsWith('studio-prompt') || s.name === 'studio-verify-tool' || s.name === 'agent-author';
    if (plane === 'mcp-tool') return s.name === 'tool-onboard' || s.name === 'deploy-readiness' || s.name === 'security-review';
    return true;
  });
  const otherSkills = skills.filter(s => !relevantSkills.includes(s));

  async function loadSkillBody(name: string) {
    if (expandedSkill === name) { setExpandedSkill(null); setSkillBody(null); return; }
    setExpandedSkill(name);
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/barnskills/${name}`);
      const data = await res.json();
      setSkillBody(data.summary || data.title || 'No body available');
    } catch {
      setSkillBody('Failed to load skill');
    }
    setLoading(false);
  }

  function SkillRow({ s, highlight }: { s: SkillMeta; highlight: boolean }) {
    const isExpanded = expandedSkill === s.name;
    return (
      <div className={`border-b border-tower-border/20 ${highlight ? 'bg-success/5' : ''}`}>
        <button
          onClick={() => loadSkillBody(s.name)}
          className="w-full text-left px-2 py-1.5 text-xs hover:bg-tower-border/20 flex items-start gap-2"
        >
          <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${highlight ? 'bg-success' : 'bg-tower-muted'}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className={`font-mono ${highlight ? 'text-success' : 'text-tower-accent'}`}>{s.name}</span>
              <span className="text-[9px] text-tower-muted">K{s.authorityRequired}</span>
            </div>
            <div className="text-[10px] text-tower-muted truncate">{s.summary || s.title}</div>
          </div>
          <span className={`text-tower-muted text-[10px] transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▸</span>
        </button>
        {isExpanded && (
          <div className="px-3 pb-2 text-[11px] text-tower-muted leading-relaxed border-l-2 border-success/30 ml-3">
            {loading ? (
              <span className="animate-pulse">Loading...</span>
            ) : (
              <>
                <div className="mb-1.5">{skillBody}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {s.triggers?.slice(0, 3).map(t => (
                    <span key={t} className="text-[9px] bg-tower-border/40 px-1 py-0.5 rounded">{t}</span>
                  ))}
                </div>
                <div className="text-[9px] text-tower-muted mt-1">
                  Authority: {AUTHORITY_LABELS[s.authorityRequired] || `K${s.authorityRequired}`} · {s.category}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-2 py-1 border-b border-tower-border/50 shrink-0">
        <div className="text-xs text-tower-muted font-semibold">BarnSkills</div>
        <div className="text-[10px] text-tower-muted">
          {plane ? `Relevant for ${plane} editing` : 'Select an artifact to see relevant skills'}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {relevantSkills.length > 0 && (
          <div>
            <div className="text-[9px] text-success font-semibold px-2 pt-2 pb-1 uppercase tracking-wider">Relevant</div>
            {relevantSkills.map(s => <SkillRow key={s.name} s={s} highlight={true} />)}
          </div>
        )}
        {otherSkills.length > 0 && (
          <div>
            <div className="text-[9px] text-tower-muted font-semibold px-2 pt-2 pb-1 uppercase tracking-wider">All Skills</div>
            {otherSkills.map(s => <SkillRow key={s.name} s={s} highlight={false} />)}
          </div>
        )}
        {skills.length === 0 && (
          <div className="p-3 text-xs text-tower-muted text-center">No skills found — seed via /api/barnskills/seed</div>
        )}
      </div>
    </div>
  );
}
